/**
 * Clips: la parte PURA de la pantalla de video.
 *
 * Reparto de trabajo de la pestaña Clips, que es el clip end-to-end de la demo
 * (*"filtrá los ataques del 14 en rotación 1… ahí están los seis videos, desde
 * las cuatro cámaras"*):
 *
 *   1. el API dice QUÉ cortar  → `GET /api/matches/:id/clips` (playlist)
 *   2. el VAR de la cancha CORTA → `exportClip` + `clipStatus` por LAN
 *   3. el navegador baja el archivo del VAR, directo
 *
 * Acá vive todo lo del medio que se puede probar sin React ni red: pasar de un
 * `Clip` (timecode + roll) a la ventana `{inNs, outNs}` que entiende el VAR,
 * llevar la cuenta de los jobs de export y resumirla para el encabezado.
 *
 * El componente sólo hace ciclo de vida (pedir, pollear, desmontar).
 */
import type { Clip } from './api-client';

const NS_PER_SECOND = 1_000_000_000;

/** Ventana del corte en el timecode maestro del VAR. */
export interface ClipWindow {
  inNs: number;
  outNs: number;
}

/**
 * `videoNs - pre` … `videoNs + post`, en nanosegundos.
 *
 * El pre-roll se recorta en 0: un ataque marcado en el segundo 3 del archivo
 * no puede pedir 8 segundos antes, y mandar un `in_ns` negativo es la clase de
 * cosa que un server en C++ contesta con un 500 o, peor, con un clip vacío.
 */
export function clipWindow(clip: Pick<Clip, 'videoNs' | 'preSeconds' | 'postSeconds'>): ClipWindow {
  const inNs = Math.max(0, Math.round(clip.videoNs - clip.preSeconds * NS_PER_SECOND));
  const outNs = Math.round(clip.videoNs + clip.postSeconds * NS_PER_SECOND);
  return { inNs, outNs };
}

/** Duración del corte en segundos (para mostrarla al lado de la etiqueta). */
export function clipDurationSeconds(clip: Pick<Clip, 'preSeconds' | 'postSeconds'>): number {
  return clip.preSeconds + clip.postSeconds;
}

/** `1_845_000_000_000` → `'00:30:45'`. El operador del VAR habla en timecode. */
export function formatTimecode(videoNs: number): string {
  const totalSeconds = Math.max(0, Math.floor(videoNs / NS_PER_SECOND));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

// ---------------------------------------------------------------------------
// Estado de los exports
// ---------------------------------------------------------------------------

/**
 * Un clip pedido al VAR, del lado del navegador.
 *
 * `pending` es nuestro (todavía no contestó el `exportClip`); los otros cuatro
 * son los del contrato `ReplayClipJob`. Se distinguen porque un pedido que no
 * salió no se puede pollear ni reintentar igual que uno encolado.
 */
export type ClipJobState = 'pending' | 'queued' | 'running' | 'done' | 'error';

export interface ClipJob {
  eventId: string;
  state: ClipJobState;
  jobId?: string;
  url?: string;
  /** 0..1 si el server lo informa. */
  progress?: number;
  error?: string;
  /** Cámaras con las que se pidió (se muestran en la fila). */
  cameras: string[];
}

export type ClipJobs = Readonly<Record<string, ClipJob>>;

export function startJob(jobs: ClipJobs, eventId: string, cameras: readonly string[]): ClipJobs {
  return { ...jobs, [eventId]: { eventId, state: 'pending', cameras: [...cameras] } };
}

/** Aplica lo que contestó el VAR sobre un job ya empezado. */
export function patchJob(jobs: ClipJobs, eventId: string, patch: Partial<ClipJob>): ClipJobs {
  const current = jobs[eventId];
  if (!current) return jobs;
  return { ...jobs, [eventId]: { ...current, ...patch } };
}

export function failJob(jobs: ClipJobs, eventId: string, error: string): ClipJobs {
  return patchJob(jobs, eventId, { state: 'error', error });
}

/** Jobs que hay que seguir polleando (los que ya tienen id y no terminaron). */
export function pollableJobs(jobs: ClipJobs): ClipJob[] {
  return Object.values(jobs).filter(
    (job) => job.jobId !== undefined && (job.state === 'queued' || job.state === 'running'),
  );
}

export interface ClipJobsSummary {
  total: number;
  done: number;
  working: number;
  failed: number;
}

/**
 * Resumen para el encabezado. Es lo que se dice en voz alta en la reunión
 * ("seis clips, cuatro listos"), así que se cuenta acá y no en el JSX.
 */
export function summarizeJobs(jobs: ClipJobs): ClipJobsSummary {
  const all = Object.values(jobs);
  return {
    total: all.length,
    done: all.filter((j) => j.state === 'done').length,
    working: all.filter((j) => j.state !== 'done' && j.state !== 'error').length,
    failed: all.filter((j) => j.state === 'error').length,
  };
}

// ---------------------------------------------------------------------------
// Filtros de la pantalla
// ---------------------------------------------------------------------------

/** Fundamentos que ofrece el selector, en el orden en que ocurren en el rally. */
export const CLIP_SKILLS = [
  'serve',
  'reception',
  'set',
  'attack',
  'block',
  'dig',
  'freeball',
] as const;
export type ClipSkill = (typeof CLIP_SKILLS)[number];

/** Los seis grados de la escala, en orden de mejor a peor (igual que el resto). */
export const CLIP_EVALUATIONS = ['#', '+', '!', '-', '/', '='] as const;

export const ROTATIONS = [1, 2, 3, 4, 5, 6] as const;

/**
 * Cuántos archivos salen de un pedido: un clip por cámara.
 *
 * Es la cuenta de la frase de la demo —seis clips por cuatro cámaras son
 * veinticuatro archivos— y conviene tenerla a la vista ANTES de apretar el
 * botón: es lo que va a tardar el VAR y lo que va a ocupar el disco.
 */
export function filesToExport(clipCount: number, cameraCount: number): number {
  return clipCount * Math.max(1, cameraCount);
}
